import React, { useMemo } from 'react';
import {
  Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ComposedChart
} from 'recharts';
import type { UptimeCheck, TimeRange } from './types';

interface ResponseTimeChartProps {
  checks: UptimeCheck[];
  timeRange: TimeRange;
}

function formatXLabel(dateStr: string, range: TimeRange): string {
  const d = new Date(dateStr);
  if (range === '3h' || range === '24h') {
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatYLabel(value: number): string {
  if (value >= 1000) return `${(value / 1000).toFixed(1)}s`;
  return `${value}ms`;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  
  // Use the raw data object from the first payload item to get all fields even if not plotted
  const entry = payload[0].payload;
  const total = entry.total_time_ms;
  const download = entry.download_ms;
  const server = entry.server_ms;
  const ssl = entry.ssl_ms;
  const tcp = entry.tcp_ms;
  const dns = entry.dns_ms;

  const format = (ms: number | null) => {
    if (ms === null || ms === undefined || ms === 0) return '—';
    return ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${Math.round(ms)}ms`;
  };

  // Check if we have any granular network data
  const hasGranular = (dns > 0 || tcp > 0 || ssl > 0);

  return (
    <div className="rounded-xl border border-border bg-card p-3 shadow-2xl text-[11px] min-w-[200px]">
      <div className="flex items-center justify-between mb-2 pb-2 border-b border-border/50">
        <p className="font-semibold text-muted-foreground">{label}</p>
        <span className="text-emerald-500 font-bold flex items-center gap-1">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {entry.status === 'down' ? 'Down' : 'Up'}
        </span>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-foreground font-bold mb-1 border-b border-border/30 pb-1">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-sm bg-[#3b82f6]" />
            <span>Total Response Time</span>
          </div>
          <span className="text-[#3b82f6]">{total >= 1000 ? `${(total / 1000).toFixed(2)}s` : `${Math.round(total)}ms`}</span>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-sm bg-[#f472b6]" />
            <span className="text-muted-foreground">Content Download</span>
          </div>
          <span className="font-medium text-foreground tabular-nums">{format(download)}</span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-sm bg-[#f97316]" />
            <span className="text-muted-foreground">Server Processing</span>
          </div>
          <span className="font-medium text-foreground tabular-nums">{format(server)}</span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-sm bg-[#4b5563]" />
            <span className="text-muted-foreground">SSL Handshake</span>
          </div>
          <span className="font-medium text-foreground tabular-nums">{format(ssl)}</span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-sm bg-[#ef4444]" />
            <span className="text-muted-foreground">TCP Connection</span>
          </div>
          <span className="font-medium text-foreground tabular-nums">{format(tcp)}</span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-sm bg-[#eab308]" />
            <span className="text-muted-foreground">DNS Lookup</span>
          </div>
          <span className="font-medium text-foreground tabular-nums">{format(dns)}</span>
        </div>

        {!hasGranular && (
          <p className="text-[9px] text-muted-foreground italic mt-2 border-t border-border/30 pt-1">
            * Network breakdown only available for manual checks.
          </p>
        )}
      </div>
    </div>
  );
};

export const ResponseTimeChart: React.FC<ResponseTimeChartProps> = ({ checks, timeRange }) => {
  const data = useMemo(() => {
    const upChecks = checks.filter(c => c.status === 'up' && c.total_time_ms != null);
    if (!upChecks.length) return [];
    const sorted = [...upChecks].sort((a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
    return sorted.map(c => ({
      time: formatXLabel(c.created_at, timeRange),
      fullTime: c.created_at,
      total_time_ms: c.total_time_ms ? Math.round(c.total_time_ms) : 0,
      dns_ms: c.dns_ms ? Math.round(c.dns_ms) : 0,
      tcp_ms: c.tcp_ms ? Math.round(c.tcp_ms) : 0,
      ssl_ms: c.ssl_ms ? Math.round(c.ssl_ms) : 0,
      server_ms: c.server_ms ? Math.round(c.server_ms) : 0,
      download_ms: c.download_ms ? Math.round(c.download_ms) : 0,
    }));
  }, [checks, timeRange]);

  if (!data.length) {
    return (
      <div className="flex items-center justify-center h-48 text-sm text-muted-foreground">
        No response time data available for this period
      </div>
    );
  }

  const maxVal = Math.max(...data.map(d => d.total_time_ms ?? 0));
  const yMax = Math.ceil(maxVal * 1.2 / 500) * 500;

  return (
    <div className="h-64 mt-4 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15}/>
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
            </linearGradient>
            <linearGradient id="colorServer" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.4} vertical={false} />
          <XAxis
            dataKey="time"
            tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            tickFormatter={formatYLabel}
            tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            tickLine={false}
            axisLine={false}
            width={70}
            domain={[0, yMax || 1000]}
          />
          <Tooltip content={<CustomTooltip />} />
          
          {/* Server Processing Time (Orange) */}
          <Area
            type="monotone"
            dataKey="server_ms"
            stroke="#f97316"
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorServer)"
            dot={false}
            connectNulls
          />

          {/* Total Response Time (Blue) */}
          <Area
            type="monotone"
            dataKey="total_time_ms"
            stroke="#3b82f6"
            strokeWidth={2.5}
            fillOpacity={1}
            fill="url(#colorTotal)"
            dot={false}
            connectNulls
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
